import { useNavigate, useSearchParams } from 'react-router';

import type { KeyboardEvent } from 'react';

import { urls } from 'Router';
import { Typo } from 'Themes/typo';

import { Spacing } from '@themes/spacing';

import type { CourseModel } from '../../../data';
import { useSelectCourseParticipants } from '../../../data';
import { useCourseOrganizationDisplay } from '../../../hooks/use-course-organization-display';
import {
  getActiveParticipantsCount,
  getExpectedParticipantsCount,
  getJoinedParticipantsCount,
  isCourseInSession
} from '../../../utils/participants-utils';
import { CourseDatesDisplay } from '../../dates/course-dates-display';

import { OrganizationText } from '../course-shared-styles';
import { CourseTagsDisplay } from '../course-tags-display';
import { ParticipantsInfo } from '../participants-info';

import { ColumnContent, DateText, StyledCard, TitleText } from './simple-course-card.styles';

type Props = {
  course: CourseModel;
};

export const SimpleCourseCard = ({ course }: Readonly<Props>) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentQuery = searchParams.toString();
  const withQuery = (url: string) => (currentQuery ? `${url}?${currentQuery}` : url);
  const courseParticipants = useSelectCourseParticipants(course.id);

  const shouldDisplayCoursePanel = !!(course.id && !course.flags?.isParticipant);
  const isCardClickable = shouldDisplayCoursePanel;

  const handleCardClick = () => {
    if (!isCardClickable) {
      return;
    }

    if (shouldDisplayCoursePanel) {
      if (course.hasFreshStart || course.flags?.isFinished) {
        navigate(withQuery(urls.coursePage(course.id)));
      } else {
        navigate(withQuery(urls.courseSettingsPage(course.id)));
      }
    } else {
      navigate(withQuery(urls.coursePage(course.id)));
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isCardClickable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const organizationDisplay = useCourseOrganizationDisplay(course);

  const showParticipantsInfo = isCourseInSession(course) && !course.flags?.isParticipant;
  const activeCount = getActiveParticipantsCount(courseParticipants, course);
  const joinedCount = getJoinedParticipantsCount(courseParticipants, course);
  const expectedCount = getExpectedParticipantsCount(course);

  return (
    <StyledCard
      isClickable={isCardClickable}
      onClick={isCardClickable ? handleCardClick : undefined}
      onKeyDown={isCardClickable ? handleKeyDown : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      role={isCardClickable ? 'button' : undefined}
      aria-label={isCardClickable ? `View course: ${course.title}` : undefined}
    >
      <ColumnContent>
        <OrganizationText variant="body2">{organizationDisplay}</OrganizationText>
      </ColumnContent>

      <ColumnContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.small }}>
          <TitleText variant={Typo.title}>{course.title}</TitleText>
          {showParticipantsInfo && (
            <ParticipantsInfo active={activeCount} joined={joinedCount} expected={expectedCount} />
          )}
        </div>
      </ColumnContent>

      <ColumnContent>
        <DateText variant="body2">
          <CourseDatesDisplay dates={course.dates} courseTimezone={course.timezone} />
        </DateText>
      </ColumnContent>

      <ColumnContent>
        <CourseTagsDisplay course={course} />
      </ColumnContent>
    </StyledCard>
  );
};
